/* ================================================================
   AdministrationDashboardPage — MINESEC Edition v3.1
   Date: 2026-08-05
   ChangeLog:
     - Permissions via hook réactif (plus getState)
     - checkPerm / checkAnyPerm stabilisés avec useCallback
     - Dépendances useMemo corrigées
   ================================================================ */

import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  TrendingUp, GraduationCap, BookOpen, UserCheck, Users,
  Shield, FileSearch, Building2, Activity, BarChart3,
  Clock, ArrowUpRight, ArrowDownRight, RefreshCw, AlertCircle,
  School, Eye, Filter, FileBarChart2, ClipboardList, Calculator,
  Percent, Award, AlertTriangle, RotateCcw, ListChecks, FileText,
  Wallet, Newspaper, CalendarDays,
  Banknote, Receipt, MessageSquare
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { useAuthStore } from '@/store/authStore'
import { schoolApi, adminApi, apiService } from '@/services/api'

/* ───────────────────────────────────────────────
   AdministrationDashboardPage — MINESEC Edition
   Adaptation Cameroun : rapports scolaires officiels
   ─────────────────────────────────────────────── */

interface AcademicYear {
  id: number
  label: string
  start_date: string
  end_date: string
  is_current: boolean
  is_selected: boolean
  school_id: number
}

interface SchoolInfo {
  id: number
  name: string
  acronym: string
}

interface DashboardStats {
  totalUsers: number
  totalStudents: number
  totalTeachers: number
  totalClasses: number
  totalSubjects: number
  auditLogsCount: number
  activeRoles: number
  publishedContent: number
  pendingApprovals: number
}

const t = {
  fr: {
    title: 'Tableau de bord',
    subtitle: "Vue d'ensemble de l'établissement — MINESEC",
    loading: 'Chargement...',
    refresh: 'Actualiser',
    totalUsers: 'Utilisateurs',
    totalStudents: 'Élèves',
    totalTeachers: 'Enseignants',
    totalClasses: 'Classes',
    totalSubjects: 'Matières',
    auditLogs: 'Logs audit',
    activeRoles: 'Rôles actifs',
    publishedContent: 'Contenu publié',
    pendingApprovals: 'En attente',
    quickStats: 'Statistiques rapides',
    schoolActivity: "Activité de l'établissement",
    overview: "Vue d'ensemble",
    management: 'Gestion',
    studentsPerClass: 'Élèves par classe',
    teacherLoad: 'Charge enseignante',
    attendanceRate: 'Taux de présence',
    budgetStatus: 'État du budget',
    activeYear: 'Année scolaire en cours',
    yearLabel: 'Année',
    noYear: 'Aucune année sélectionnée',
    showDebug: 'Debug',
    hideDebug: 'Masquer',
    allSchools: 'Toutes les écoles',
    minesecReports: 'Rapports & Statistiques MINESEC',
    minesecReportsDesc: 'Documents officiels et suivis pédagogiques',
    groupScolarite: 'Scolarité',
    groupPv: 'Procès-verbaux',
    groupResults: 'Résultats & Orientations',
    listProvisoire: 'Liste provisoire',
    listDefinitive: 'Liste définitive',
    effectifsClasses: 'Effectif des classes',
    ficheSaisieNotes: 'Fiche de saisie des notes',
    pvTrimestrielle: 'PV Trimestrielle',
    pvAnnuelle: 'PV Annuelle',
    livretBepcDecc: 'Livret BEPC DECC',
    listMajors: 'Liste des majors',
    tauxReussite: 'Taux de réussite',
    listAdmis: 'Admis (conseil)',
    listExclus: 'Exclus (conseil)',
    listRedoublants: 'Redoublants (conseil)',
    moduleStudents: 'Inscription',
    moduleTimetable: 'Pédagogie',
    moduleAcademics: 'Académique',
    moduleGrades: 'Évaluation',
    moduleStatistics: 'Statistiques',
    moduleBudget: 'Budget',
    moduleAdmin: 'Administration',
    moduleContent: 'Contenu',
    moduleDiscipline: 'Discipline',
    moduleFees: 'Scolarité',
    moduleMessages: 'Communication',
  },
  en: {
    title: 'Dashboard',
    subtitle: 'School overview — MINESEC',
    loading: 'Loading...',
    refresh: 'Refresh',
    totalUsers: 'Users',
    totalStudents: 'Students',
    totalTeachers: 'Teachers',
    totalClasses: 'Classes',
    totalSubjects: 'Subjects',
    auditLogs: 'Audit logs',
    activeRoles: 'Active roles',
    publishedContent: 'Published content',
    pendingApprovals: 'Pending',
    quickStats: 'Quick Stats',
    schoolActivity: 'School Activity',
    overview: 'Overview',
    management: 'Management',
    studentsPerClass: 'Students per class',
    teacherLoad: 'Teacher workload',
    attendanceRate: 'Attendance rate',
    budgetStatus: 'Budget status',
    activeYear: 'Current Academic Year',
    yearLabel: 'Year',
    noYear: 'No year selected',
    showDebug: 'Debug',
    hideDebug: 'Hide',
    allSchools: 'All schools',
    minesecReports: 'MINESEC Reports & Statistics',
    minesecReportsDesc: 'Official documents and academic tracking',
    groupScolarite: 'Enrollment',
    groupPv: 'Transcripts',
    groupResults: 'Results & Orientation',
    listProvisoire: 'Provisional List',
    listDefinitive: 'Definitive List',
    effectifsClasses: 'Class Enrollments',
    ficheSaisieNotes: 'Grade Entry Form',
    pvTrimestrielle: 'Quarterly Transcript',
    pvAnnuelle: 'Annual Transcript',
    livretBepcDecc: 'BEPC DECC Booklet',
    listMajors: 'Top Students',
    tauxReussite: 'Pass Rates',
    listAdmis: 'Admitted (council)',
    listExclus: 'Excluded (council)',
    listRedoublants: 'Repeating (council)',
    moduleStudents: 'Enrollment',
    moduleTimetable: 'Pedagogy',
    moduleAcademics: 'Academic',
    moduleGrades: 'Assessment',
    moduleStatistics: 'Statistics',
    moduleBudget: 'Budget',
    moduleAdmin: 'Administration',
    moduleContent: 'Content',
    moduleDiscipline: 'Discipline',
    moduleFees: 'School Fees',
    moduleMessages: 'Communication',
  }
}

function extractArray(response: any): any[] {
  if (!response) return []
  if (Array.isArray(response)) return response
  const res = response as any
  if (Array.isArray(res.data)) return res.data
  if (Array.isArray(res.items)) return res.items
  if (Array.isArray(res.results)) return res.results
  if (res.data && Array.isArray(res.data.items)) return res.data.items
  if (res.data && Array.isArray(res.data.results)) return res.data.results
  const possibleKeys = ['teachers', 'users', 'students', 'classes', 'subjects', 'logs', 'roles', 'items', 'results', 'data']
  for (const key of possibleKeys) {
    if (Array.isArray(res[key])) return res[key]
    if (res.data && Array.isArray(res.data[key])) return res.data[key]
  }
  return []
}

function extractTotal(response: any): number | null {
  if (!response) return null
  const res = response as any
  if (typeof res.total === 'number') return res.total
  if (res.data && typeof res.data.total === 'number') return res.data.total
  if (typeof res.count === 'number') return res.count
  if (res.data && typeof res.data.count === 'number') return res.data.count
  return null
}

async function safeApiCall(
  apiMethod: ((params?: any) => Promise<any>) | null | undefined,
  label: string,
  params: Record<string, any> = {},
  debugMsgs: string[] = []
): Promise<{ count: number; error: string | null }> {
  if (!apiMethod || typeof apiMethod !== 'function') {
    debugMsgs.push(`${label}: not available`)
    return { count: 0, error: 'not available' }
  }
  try {
    const res = await apiMethod(Object.keys(params).length > 0 ? params : undefined)
    const total = extractTotal(res)
    const arr = extractArray(res)
    const count = total !== null ? total : arr.length
    debugMsgs.push(`${label}: ${count}`)
    return { count, error: null }
  } catch (e: any) {
    const status = e.response?.status || '?';
    const msg = e.response?.data?.detail || e.message || 'Unknown'
    debugMsgs.push(`${label}: ${status}`)
    return { count: 0, error: `${status}: ${msg}` }
  }
}

export default function AdministrationDashboardPage() {
  const { lang } = useLang()
  const txt = t[lang]
  const { user, permissions } = useAuthStore()

  const userSchoolId = user?.school_id ?? null

  // ─── PERMISSIONS VIA HOOK RÉACTIF ───
  const rawPerms: string[] = useMemo(() => {
    if (Array.isArray(permissions)) return permissions
    if (typeof permissions === 'object' && permissions !== null) {
      return Object.entries(permissions as Record<string, boolean>)
        .filter(([, v]) => v === true)
        .map(([k]) => k)
    }
    return []
  }, [permissions])

  const rawRole = useMemo(() => (user?.role || '').toLowerCase(), [user?.role])
  const isFullAccess = useMemo(() => ['super_admin', 'admin', 'proviseur'].includes(rawRole), [rawRole])

  // ─── CHECK PERMISSION STABILISÉ ───
  const checkPerm = useCallback((perm: string | string[]): boolean => {
    if (isFullAccess) return true
    if (!Array.isArray(rawPerms) || rawPerms.length === 0) return false
    if (Array.isArray(perm)) {
      return perm.some((p) => rawPerms.includes(p))
    }
    return rawPerms.includes(perm)
  }, [rawPerms, isFullAccess])

  const checkAnyPerm = useCallback((perm: string | string[]): boolean => checkPerm(perm), [checkPerm])

  const [selectedYear, setSelectedYear] = useState<AcademicYear | null>(null)
  const [yearsLoading, setYearsLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(true)
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0, totalStudents: 0, totalTeachers: 0,
    totalClasses: 0, totalSubjects: 0, auditLogsCount: 0, activeRoles: 0,
    publishedContent: 0, pendingApprovals: 0
  })
  const [debugInfo, setDebugInfo] = useState<string>('')
  const [showDebug, setShowDebug] = useState(false)

  const [schools, setSchools] = useState<SchoolInfo[]>([])
  const [selectedSchoolFilter, setSelectedSchoolFilter] = useState<number | null>(null)

  const loadSchools = useCallback(async () => {
    try {
      if (schoolApi && typeof schoolApi.getSchools === 'function') {
        const res = await schoolApi.getSchools()
        const arr = extractArray(res)
        if (arr.length > 0) {
          setSchools(arr.map((s: any) => ({
            id: s.id,
            name: s.name || s.label || s.acronym || `School ${s.id}`,
            acronym: s.acronym || ''
          })))
        }
      }
    } catch (e) { /* silent */ }
  }, [])

  const loadSelectedYear = useCallback(async () => {
    setYearsLoading(true)
    try {
      if (!userSchoolId) {
        setSelectedYear(null)
        return
      }
      let years: AcademicYear[] = []
      if (schoolApi && typeof schoolApi.getAcademicYears === 'function') {
        try {
          const yearsRes = await schoolApi.getAcademicYears(userSchoolId)
          years = extractArray(yearsRes)
        } catch (e) { /* silent */ }
      }
      if (years.length > 0) {
        const selected = years.find((y: AcademicYear) => y.is_selected === true && y.school_id === userSchoolId)
        const current = years.find((y: AcademicYear) => y.is_current === true && y.school_id === userSchoolId)
        const found = selected || current || years.find((y: AcademicYear) => y.school_id === userSchoolId) || years[0]
        setSelectedYear(found)
      } else {
        setSelectedYear(null)
      }
    } catch (err) {
      setSelectedYear(null)
    } finally {
      setYearsLoading(false)
    }
  }, [userSchoolId])

  const loadStats = useCallback(async (schoolId?: number | null, yearId?: number | null) => {
    setStatsLoading(true)
    setDebugInfo('')
    const debugMsgs: string[] = []

    const effectiveSchoolId = schoolId ?? selectedSchoolFilter ?? userSchoolId
    const effectiveYearId = yearId ?? selectedYear?.id ?? null

    debugMsgs.push(`Loading: school=${effectiveSchoolId}, year=${effectiveYearId}`)

    try {
      const baseParams: Record<string, any> = {}
      if (effectiveSchoolId) baseParams.school_id = effectiveSchoolId
      if (effectiveYearId) baseParams.academic_year_id = effectiveYearId

      const usersRes = await safeApiCall(adminApi?.getUsers, 'Users', baseParams, debugMsgs)
      const studentsRes = await safeApiCall(apiService?.getStudents, 'Students', baseParams, debugMsgs)

      let teachersRes = await safeApiCall(apiService?.getTeachers, 'Teachers', baseParams, debugMsgs)
      if (teachersRes.count === 0 && !teachersRes.error && effectiveSchoolId) {
        const allParams = { ...baseParams }
        delete allParams.school_id
        const retryRes = await safeApiCall(apiService?.getTeachers, 'Teachers(all)', allParams, debugMsgs)
        if (retryRes.count > 0) teachersRes = retryRes
      }

      const classesRes = await safeApiCall(apiService?.getClasses, 'Classes', baseParams, debugMsgs)
      const subjectsRes = await safeApiCall(apiService?.getSubjects, 'Subjects', baseParams, debugMsgs)
      const auditRes = await safeApiCall(apiService?.getAuditLogs, 'Audit', baseParams, debugMsgs)
      const rolesRes = await safeApiCall(adminApi?.getRoles, 'Roles', {}, debugMsgs)

      setStats({
        totalUsers: usersRes.count,
        totalStudents: studentsRes.count,
        totalTeachers: teachersRes.count,
        totalClasses: classesRes.count,
        totalSubjects: subjectsRes.count,
        auditLogsCount: auditRes.count,
        activeRoles: rolesRes.count,
        publishedContent: 0,
        pendingApprovals: 0,
      })

      debugMsgs.push('Done ✓')

    } catch (err: any) {
      debugMsgs.push(`Error: ${err.message}`)
    } finally {
      setStatsLoading(false)
      setDebugInfo(debugMsgs.join('\n'))
    }
  }, [userSchoolId, selectedSchoolFilter, selectedYear])

  useEffect(() => {
    const handleYearChange = (e: any) => {
      const detail = e.detail
      if (detail?.year_id) {
        loadSelectedYear().then(() => {
          loadStats(detail.school_id, detail.year_id)
        })
      }
    }
    window.addEventListener('lybibal-year-change', handleYearChange)
    return () => window.removeEventListener('lybibal-year-change', handleYearChange)
  }, [loadSelectedYear, loadStats])

  useEffect(() => {
    loadSchools()
    loadSelectedYear()
  }, [loadSchools, loadSelectedYear])

  useEffect(() => {
    if (selectedYear) {
      loadStats(selectedYear.school_id, selectedYear.id)
    }
  }, [selectedYear, loadStats])

  const formatNumber = (n: number) => n.toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US')

  const statCards = useMemo(() => [
    { label: txt.totalStudents, value: stats.totalStudents, icon: GraduationCap, color: 'bg-emerald-500', trend: '+12%', up: true },
    { label: txt.totalTeachers, value: stats.totalTeachers, icon: UserCheck, color: 'bg-blue-500', trend: '+3%', up: true },
    { label: txt.totalClasses, value: stats.totalClasses, icon: Building2, color: 'bg-amber-500', trend: '+5%', up: true },
    { label: txt.totalSubjects, value: stats.totalSubjects, icon: BookOpen, color: 'bg-rose-500', trend: '0%', up: true },
    { label: txt.totalUsers, value: stats.totalUsers, icon: Shield, color: 'bg-slate-500', trend: '+8%', up: true },
    { label: txt.auditLogs, value: stats.auditLogsCount, icon: FileSearch, color: 'bg-cyan-500', trend: '+24%', up: true },
    { label: txt.activeRoles, value: stats.activeRoles, icon: Activity, color: 'bg-indigo-500', trend: '0%', up: true },
  ], [txt, stats])

  // ─── FILTRAGE PAR PERMISSIONS ───
  const PERMISSION_MAP: Record<string, string | string[]> = {
    '/students': 'students_read',
    '/timetable': 'timetable_read',
    '/academics': ['specialties_read', 'levels_read', 'assign_student', 'class_council_read', 'class_council_express', 'discipline_read', 'discipline_write'],
    '/discipline': ['discipline_read', 'discipline_write'],
    '/grades': 'grades_read',
    '/statistics': 'statistics_read',
    '/budget': 'budget_read',
    '/settings': 'settings_read',
    '/settings/users': 'users_read',
    '/settings/permissions': 'admin_full',
    '/audit': 'audit_read',
    '/content': 'news_publish',
    '/fees': 'fees_read',
    '/fees/types': 'fees_manage',
    '/fees/structures': 'fees_manage',
    '/fees/reports': 'fees_read',
    '/messages': ['messages_read', 'messages_manage'],
    '/approvals': 'grades_approve',
    '/statistics/scolarite/provisoire': 'list_provisoire_read',
    '/statistics/scolarite/definitive': 'list_definitive_read',
    '/statistics/scolarite/effectifs': 'effectifs_read',
    '/statistics/scolarite/saisie-notes': 'saisie_notes_read',
    '/statistics/proces-verbaux/trimestrielle': 'pv_read',
    '/statistics/proces-verbaux/annuelle': 'pv_read',
    '/statistics/livret-bepc-decc': 'livret_read',
    '/statistics/majors': 'majors_read',
    '/statistics/taux-reussite': 'taux_reussite_read',
    '/statistics/admis': 'decisions_conseil_read',
    '/statistics/exclus': 'decisions_conseil_read',
    '/statistics/redoublants': 'decisions_conseil_read',
  }

  const _minesecReports = useMemo(() => [
    { label: txt.listProvisoire, path: '/statistics/scolarite/provisoire', icon: ClipboardList, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100', group: 'scolarite' },
    { label: txt.listDefinitive, path: '/statistics/scolarite/definitive', icon: ClipboardList, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100', group: 'scolarite' },
    { label: txt.effectifsClasses, path: '/statistics/scolarite/effectifs', icon: Users, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100', group: 'scolarite' },
    { label: txt.ficheSaisieNotes, path: '/statistics/scolarite/saisie-notes', icon: Calculator, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-100', group: 'scolarite' },
    { label: txt.pvTrimestrielle, path: '/statistics/proces-verbaux/trimestrielle', icon: FileText, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-100', group: 'pv' },
    { label: txt.pvAnnuelle, path: '/statistics/proces-verbaux/annuelle', icon: FileText, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-100', group: 'pv' },
    { label: txt.livretBepcDecc, path: '/statistics/livret-bepc-decc', icon: BookOpen, color: 'text-cyan-600', bg: 'bg-cyan-50', border: 'border-cyan-100', group: 'results' },
    { label: txt.listMajors, path: '/statistics/majors', icon: Award, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-100', group: 'results' },
    { label: txt.tauxReussite, path: '/statistics/taux-reussite', icon: Percent, color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-100', group: 'results' },
    { label: txt.listAdmis, path: '/statistics/admis', icon: ListChecks, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100', group: 'results' },
    { label: txt.listExclus, path: '/statistics/exclus', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-100', group: 'results' },
    { label: txt.listRedoublants, path: '/statistics/redoublants', icon: RotateCcw, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-100', group: 'results' },
  ], [txt])

  const minesecReports = useMemo(() => {
    return _minesecReports.filter((item) => {
      const perm = PERMISSION_MAP[item.path]
      if (!perm) return true
      return checkAnyPerm(perm)
    })
  }, [_minesecReports, checkAnyPerm])

  const _quickModules = useMemo(() => [
    { label: txt.moduleStudents, path: '/students', icon: Users, color: 'bg-blue-500' },
    { label: txt.moduleTimetable, path: '/timetable', icon: CalendarDays, color: 'bg-violet-500' },
    { label: txt.moduleAcademics, path: '/academics', icon: GraduationCap, color: 'bg-amber-500' },
    { label: txt.moduleGrades, path: '/grades', icon: BookOpen, color: 'bg-emerald-500' },
    { label: txt.moduleStatistics, path: '/statistics', icon: FileBarChart2, color: 'bg-indigo-500' },
    { label: txt.moduleBudget, path: '/budget', icon: Wallet, color: 'bg-cyan-500' },
    { label: txt.moduleAdmin, path: '/settings', icon: Shield, color: 'bg-slate-500' },
    { label: txt.moduleContent, path: '/content', icon: Newspaper, color: 'bg-rose-500' },
    { label: lang === 'fr' ? 'Discipline' : 'Discipline', path: '/discipline', icon: Shield, color: 'bg-red-500' },
    { label: txt.moduleFees, path: '/fees', icon: Banknote, color: 'bg-teal-500' },
    { label: txt.moduleMessages, path: '/messages', icon: MessageSquare, color: 'bg-pink-500' },
  ], [txt, lang])

  const quickModules = useMemo(() => {
    return _quickModules.filter((item) => {
      const perm = PERMISSION_MAP[item.path]
      if (!perm) return true
      return checkAnyPerm(perm)
    })
  }, [_quickModules, checkAnyPerm])

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-primary-600" />
            {txt.title}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {txt.subtitle}
            {selectedYear && (
              <span className="ml-2 text-primary-600 font-medium">• {selectedYear.label}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {schools.length > 1 && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={selectedSchoolFilter ?? ''}
                onChange={(e) => {
                  const val = e.target.value ? parseInt(e.target.value) : null
                  setSelectedSchoolFilter(val)
                  loadStats(val)
                }}
                className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{txt.allSchools}</option>
                {schools.map((s) => (
                  <option key={s.id} value={s.id}>{s.acronym || s.name}</option>
                ))}
              </select>
            </div>
          )}
          <button
            onClick={() => setShowDebug(!showDebug)}
            className="flex items-center gap-2 px-3 py-2.5 bg-gray-100 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-200 transition-colors"
          >
            <Eye className="w-4 h-4" />
            {showDebug ? txt.hideDebug : txt.showDebug}
          </button>
          <button
            onClick={() => { loadStats(); loadSelectedYear() }}
            disabled={statsLoading || yearsLoading}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${statsLoading || yearsLoading ? 'animate-spin' : ''}`} />
            {txt.refresh}
          </button>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary-600" />
          {txt.quickStats}
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((s, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 ${s.color} rounded-lg flex items-center justify-center`}>
                  <s.icon className="w-5 h-5 text-white" />
                </div>
                <div className={`flex items-center gap-0.5 text-xs font-medium ${s.up ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {s.up ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                  {s.trend}
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {statsLoading ? <span className="text-gray-300">—</span> : formatNumber(s.value)}
              </p>
              <p className="text-sm text-gray-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* MINESEC Reports Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileBarChart2 className="w-5 h-5 text-primary-600" />
            {txt.minesecReports}
          </h2>
          <p className="text-xs text-gray-400 hidden sm:block">{txt.minesecReportsDesc}</p>
        </div>
        <div className="space-y-4">
          {(['scolarite', 'pv', 'results'] as const).map((groupKey) => {
            const groupItems = minesecReports.filter((r) => r.group === groupKey)
            if (groupItems.length === 0) return null
            const groupLabels: Record<string, string> = {
              scolarite: lang === 'fr' ? 'Scolarité' : 'Enrollment',
              pv: lang === 'fr' ? 'Procès-verbaux' : 'Transcripts',
              results: lang === 'fr' ? 'Résultats & Orientations' : 'Results & Orientation',
            }
            return (
              <div key={groupKey}>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {groupLabels[groupKey]}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {groupItems.map((item, i) => (
                    <button
                      key={i}
                      onClick={() => window.dispatchEvent(new CustomEvent('navigate-to', { detail: item.path }))}
                      className={`flex items-center gap-3 p-3 rounded-lg ${item.bg} ${item.border} border hover:shadow-sm transition-all text-left group`}
                    >
                      <div className={`w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform`}>
                        <item.icon className={`w-5 h-5 ${item.color}`} />
                      </div>
                      <span className="text-sm font-medium text-gray-700">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )
          })}
          {minesecReports.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              {lang === 'fr' ? 'Aucun rapport accessible avec vos permissions actuelles.' : 'No reports accessible with your current permissions.'}
            </p>
          )}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary-600" />
              {txt.schoolActivity}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{statsLoading ? '—' : formatNumber(stats.totalStudents)}</p>
                <p className="text-xs text-gray-500 mt-1">{txt.totalStudents}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{statsLoading ? '—' : formatNumber(stats.totalTeachers)}</p>
                <p className="text-xs text-gray-500 mt-1">{txt.totalTeachers}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{statsLoading ? '—' : formatNumber(stats.totalClasses)}</p>
                <p className="text-xs text-gray-500 mt-1">{txt.totalClasses}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{statsLoading ? '—' : formatNumber(stats.totalSubjects)}</p>
                <p className="text-xs text-gray-500 mt-1">{txt.totalSubjects}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary-600" />
              {txt.overview}
            </h3>
            <div className="space-y-5">
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-gray-600">{txt.studentsPerClass}</span>
                  <span className="font-medium text-gray-900">{stats.totalClasses > 0 ? Math.round(stats.totalStudents / stats.totalClasses) : 0}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5">
                  <div className="bg-emerald-500 h-2.5 rounded-full transition-all duration-700" style={{ width: `${Math.min((stats.totalClasses > 0 ? (stats.totalStudents / stats.totalClasses) / 40 : 0) * 100, 100)}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-gray-600">{txt.teacherLoad}</span>
                  <span className="font-medium text-gray-900">{stats.totalClasses > 0 ? Math.round(stats.totalTeachers / stats.totalClasses) : 0} {lang === 'fr' ? 'classes/ens' : 'classes/tch'}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5">
                  <div className="bg-blue-500 h-2.5 rounded-full transition-all duration-700" style={{ width: `${Math.min((stats.totalClasses > 0 ? (stats.totalTeachers / stats.totalClasses) / 3 : 0) * 100, 100)}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-gray-600">{txt.attendanceRate}</span>
                  <span className="font-medium text-gray-900">92%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5">
                  <div className="bg-amber-500 h-2.5 rounded-full transition-all duration-700" style={{ width: '92%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-gray-600">{txt.budgetStatus}</span>
                  <span className="font-medium text-gray-900">78%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5">
                  <div className="bg-violet-500 h-2.5 rounded-full transition-all duration-700" style={{ width: '78%' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary-600" />
              {txt.management}
            </h3>
            <div className="space-y-1">
              {useMemo(() => [
                { label: txt.totalUsers, value: stats.totalUsers, icon: Users, path: 'settings/users', perm: 'users_read' },
                { label: txt.activeRoles, value: stats.activeRoles, icon: Shield, path: 'settings/permissions', perm: 'admin_full' },
                { label: txt.auditLogs, value: stats.auditLogsCount, icon: FileSearch, path: 'audit', perm: 'audit_read' },
                { label: txt.publishedContent, value: stats.publishedContent, icon: BookOpen, path: 'content', perm: 'news_publish' },
                { label: txt.pendingApprovals, value: stats.pendingApprovals, icon: AlertCircle, path: 'approvals', perm: 'grades_approve' },
              ].filter(item => !item.perm || checkPerm(item.perm)), [txt, stats, checkPerm]).map((item, i) => (
                <button key={i} onClick={() => window.dispatchEvent(new CustomEvent('navigate-to', { detail: item.path }))} className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors text-left">
                  <div className="flex items-center gap-3">
                    <item.icon className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-700">{item.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{statsLoading ? '—' : formatNumber(item.value)}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl p-6 text-white">
            <div className="flex items-center gap-2 mb-3">
              <School className="w-5 h-5 opacity-80" />
              <span className="text-sm font-medium opacity-90">{txt.activeYear}</span>
            </div>
            {yearsLoading ? (
              <p className="text-lg opacity-70">{txt.loading}</p>
            ) : selectedYear ? (
              <>
                <p className="text-2xl font-bold">{selectedYear.label}</p>
                <p className="text-sm opacity-80 mt-1">{selectedYear.start_date} → {selectedYear.end_date}</p>
                <div className="mt-4 flex gap-2">
                  {selectedYear.is_current && <span className="px-2.5 py-1 bg-white/20 rounded-full text-xs font-medium backdrop-blur-sm">{lang === 'fr' ? 'Actuel' : 'Current'}</span>}
                  {selectedYear.is_selected && <span className="px-2.5 py-1 bg-white/20 rounded-full text-xs font-medium backdrop-blur-sm">{lang === 'fr' ? 'Sélectionnée' : 'Selected'}</span>}
                </div>
              </>
            ) : (
              <p className="text-lg opacity-70">{txt.noYear}</p>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary-600" />
              {lang === 'fr' ? 'Modules' : 'Modules'}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {quickModules.map((mod, i) => (
                <button
                  key={i}
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate-to', { detail: mod.path }))}
                  className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors text-center"
                >
                  <div className={`w-9 h-9 ${mod.color} rounded-lg flex items-center justify-center`}>
                    <mod.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-xs font-medium text-gray-600">{mod.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showDebug && (
        <div className="bg-gray-900 rounded-xl p-4 text-xs font-mono text-green-400 overflow-auto max-h-64">
          <p className="text-gray-400 mb-2 font-bold">Debug:</p>
          <pre className="whitespace-pre-wrap">{debugInfo}</pre>
          <div className="mt-3 pt-3 border-t border-gray-700">
            <p className="text-yellow-400 font-bold">Permissions:</p>
            <p>role={rawRole || 'none'}</p>
            <p>isFullAccess={isFullAccess ? 'true' : 'false'}</p>
            <p>permsCount={rawPerms.length}</p>
            <p>perms={JSON.stringify(rawPerms.slice(0, 30))}</p>
            <p className="text-yellow-400 font-bold mt-2">Modules visibles:</p>
            <p>quickModules={quickModules.length}</p>
            <p>minesecReports={minesecReports.length}</p>
          </div>
        </div>
      )}
    </div>
  )
}